import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ClipboardText, CheckCircle, XCircle, FileText, ArrowsClockwise,
  Lightning, Upload, Eye, ShieldCheck, Prohibit, Warning,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_STYLE = {
  validato:       { label: 'Validato BO',    bg: 'bg-cyan-50 border-cyan-200',   text: 'text-cyan-700',   dot: 'bg-cyan-500' },
  da_attivare:    { label: 'Da Attivare',    bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  in_attivazione: { label: 'In Attivazione', bg: 'bg-blue-50 border-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  attivato:       { label: 'Attivato',       bg: 'bg-green-50 border-green-200', text: 'text-green-700',  dot: 'bg-green-500' },
  respinto_bo:    { label: 'Respinto BO',    bg: 'bg-red-50 border-red-200',     text: 'text-red-700',    dot: 'bg-red-500' },
};

const STATES_ORDER = ['validato', 'da_attivare', 'in_attivazione', 'attivato', 'respinto_bo'];

export default function ContractsPage() {
  const [data, setData] = useState({ contracts: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [distributors, setDistributors] = useState([]);
  const [active, setActive] = useState(null);
  const [action, setAction] = useState(null); // 'open_switch' | 'reject_bo' | 'activate_manual' | 'upload_rcu'
  const [formData, setFormData] = useState({});
  const [rcuFile, setRcuFile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/contracts/bo/list`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
      const meta = await fetch(`${API}/api/contracts/bo/metadata`, { credentials: 'include' });
      if (meta.ok) setDistributors((await meta.json()).distributors || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.contracts.filter(c => {
    if (activeTab !== 'all' && c.bo_state !== activeTab) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [c.ragione_sociale, c.partita_iva, c.nome_offerta]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const openAction = (contract, type) => {
    setActive(contract); setAction(type);
    setFormData({
      motivo: '',
      numero_pratica_switch: contract.numero_pratica_switch || '',
      distributore: contract.distributore || '',
      data_apertura_switch: new Date().toISOString().slice(0, 10),
      esito_pratica: 'in_corso',
      mese_competenza_sii: new Date().toISOString().slice(0, 7),
    });
  };

  const runAction = async () => {
    if (!active || !action) return;
    const base = `${API}/api/contracts/${active.id}`;
    const endpoints = {
      validate_bo: { url: `${base}/validate-bo`, method: 'PATCH', body: { note: formData.motivo } },
      open_switch: { url: `${base}/open-switch`, method: 'PATCH', body: formData },
      reject_bo: { url: `${base}/reject-bo`, method: 'PATCH', body: { motivo: formData.motivo } },
      activate_manual: { url: `${base}/activate-manual`, method: 'PATCH', body: {
        mese_competenza_sii: formData.mese_competenza_sii, motivo: formData.motivo || 'forzatura manuale'
      }},
    };
    const cfg = endpoints[action];
    if (!cfg) return;
    try {
      const r = await fetch(cfg.url, {
        method: cfg.method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cfg.body),
      });
      if (r.ok) {
        toast.success('Azione eseguita');
        setActive(null); setAction(null); setFormData({});
        fetchData();
      } else {
        const d = await r.json();
        toast.error(d.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore di rete'); }
  };

  const uploadRcu = async () => {
    if (!rcuFile || !formData.mese_competenza_sii) {
      toast.error('Seleziona CSV e mese competenza'); return;
    }
    const fd = new FormData();
    fd.append('file', rcuFile);
    fd.append('mese_competenza', formData.mese_competenza_sii);
    try {
      const r = await fetch(`${API}/api/contracts/rcu-upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`RCU match: ${d.contracts_activated} contratti attivati su ${d.pods_in_csv} POD`);
        setAction(null); setRcuFile(null);
        fetchData();
      } else toast.error(d.detail || 'Errore');
    } catch { toast.error('Upload fallito'); }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento contratti...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="contracts-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardText size={32} weight="duotone" className="text-orange-500" />
            Contratti Back Office
          </h1>
          <p className="text-slate-500 mt-1">
            Gestione post-firma: validazione dati → apertura switch → attivazione tramite match RCU/SII.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData} size="sm" data-testid="reload-contracts">
            <ArrowsClockwise size={14} className="mr-1" /> Aggiorna
          </Button>
          <Button onClick={() => setAction('upload_rcu')}
                  className="bg-orange-500 hover:bg-orange-600" size="sm"
                  data-testid="upload-rcu-btn">
            <Upload size={14} className="mr-1" /> Carica CSV RCU
          </Button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 border-b mb-4 overflow-x-auto">
        <button onClick={() => setActiveTab('all')} data-testid="tab-all"
                className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === 'all' ? 'border-orange-500 text-orange-600 font-semibold'
                                       : 'border-transparent text-slate-500'
                }`}>
          Tutti <span className="ml-1 bg-slate-100 px-1.5 rounded text-[10px]">{data.contracts.length}</span>
        </button>
        {STATES_ORDER.map(st => (
          <button key={st} onClick={() => setActiveTab(st)}
                  data-testid={`tab-${st}`}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${
                    activeTab === st ? 'border-orange-500 text-orange-600 font-semibold'
                                      : 'border-transparent text-slate-500'
                  }`}>
            <span className={`w-2 h-2 rounded-full ${STATE_STYLE[st].dot}`}></span>
            {STATE_STYLE[st].label}
            <span className="bg-slate-100 px-1.5 rounded text-[10px]">
              {data.counts?.[st] || 0}
            </span>
          </button>
        ))}
      </div>

      <Input placeholder="Cerca per ragione sociale, P.IVA, CTE..."
             value={search} onChange={(e) => setSearch(e.target.value)}
             className="max-w-md mb-4" data-testid="contracts-search" />

      {/* Table */}
      <div className="border rounded-lg bg-white overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 text-xs font-semibold text-slate-600">Ragione Sociale</th>
              <th className="text-left p-3 text-xs font-semibold text-slate-600">P.IVA</th>
              <th className="text-left p-3 text-xs font-semibold text-slate-600">CTE</th>
              <th className="text-left p-3 text-xs font-semibold text-slate-600">Stato BO</th>
              <th className="text-left p-3 text-xs font-semibold text-slate-600">Pratica Switch</th>
              <th className="text-left p-3 text-xs font-semibold text-slate-600">Ultima modifica</th>
              <th className="text-right p-3 text-xs font-semibold text-slate-600">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-12 text-slate-400">
                Nessun contratto in questa categoria.
              </td></tr>
            ) : filtered.map(c => {
              const st = STATE_STYLE[c.bo_state] || { label: c.bo_state || '—', bg: 'bg-slate-50', text: 'text-slate-600' };
              return (
                <tr key={c.id} className="border-b hover:bg-orange-50/30"
                    data-testid={`contract-row-${c.id}`}>
                  <td className="p-3 font-medium text-slate-900">
                    {c.ragione_sociale}
                    {c.customer_edits_pre_sign && (
                      <div className="mt-1">
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]"
                               title={`Il cliente ha modificato: ${(c.customer_edits_fields || []).join(', ')}`}
                               data-testid={`customer-edits-badge-${c.id}`}>
                          ✏️ Firmato con modifiche cliente
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-slate-600">{c.partita_iva || '—'}</td>
                  <td className="p-3 text-xs text-slate-600">{c.nome_offerta || '—'}</td>
                  <td className="p-3">
                    <Badge className={`${st.bg} ${st.text} border text-[10px]`}>{st.label}</Badge>
                    {c.respinto_motivo && (
                      <div className="text-[10px] text-red-600 mt-1 italic">⚠ {c.respinto_motivo.slice(0, 60)}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {c.numero_pratica_switch ? (
                      <>
                        <div className="font-mono text-slate-700">{c.numero_pratica_switch}</div>
                        <div className="text-[10px] text-slate-500">{c.distributore}</div>
                      </>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3 text-[10px] text-slate-400">
                    {c.updated_at ? new Date(c.updated_at).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.bo_state === 'respinto_bo' && (
                        <button onClick={() => openAction(c, 'validate_bo')}
                                className="p-1 rounded hover:bg-cyan-50 text-cyan-600"
                                title="Rivalida" data-testid={`validate-${c.id}`}>
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {(c.bo_state === 'validato' || c.bo_state === 'da_attivare') && (
                        <button onClick={() => openAction(c, 'open_switch')}
                                className="p-1 rounded hover:bg-blue-50 text-blue-600"
                                title="Apri pratica switch" data-testid={`open-switch-${c.id}`}>
                          <Lightning size={14} />
                        </button>
                      )}
                      {c.bo_state !== 'attivato' && c.bo_state !== 'respinto_bo' && (
                        <button onClick={() => openAction(c, 'activate_manual')}
                                className="p-1 rounded hover:bg-green-50 text-green-600"
                                title="Forzatura manuale attivazione" data-testid={`activate-manual-${c.id}`}>
                          <ShieldCheck size={14} />
                        </button>
                      )}
                      {c.bo_state !== 'respinto_bo' && c.bo_state !== 'attivato' && (
                        <button onClick={() => openAction(c, 'reject_bo')}
                                className="p-1 rounded hover:bg-red-50 text-red-600"
                                title="Respingi al commerciale" data-testid={`reject-${c.id}`}>
                          <Prohibit size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action Dialog */}
      <Dialog open={!!action && action !== 'upload_rcu'} onOpenChange={(o) => { if (!o) { setActive(null); setAction(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {action === 'open_switch' && 'Apri Pratica Switch'}
              {action === 'reject_bo' && 'Respingi al Commerciale'}
              {action === 'activate_manual' && 'Forzatura Manuale Attivazione'}
              {action === 'validate_bo' && 'Rivalida Contratto'}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">{active.ragione_sociale}</div>

              {action === 'open_switch' && (
                <>
                  <div>
                    <label className="text-xs text-slate-500">Numero pratica *</label>
                    <Input value={formData.numero_pratica_switch || ''}
                           onChange={(e) => setFormData({ ...formData, numero_pratica_switch: e.target.value })}
                           data-testid="numero-pratica-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Distributore *</label>
                    <select value={formData.distributore || ''}
                            onChange={(e) => setFormData({ ...formData, distributore: e.target.value })}
                            className="w-full border rounded px-3 py-2 text-sm"
                            data-testid="distributore-select">
                      <option value="">-- Seleziona --</option>
                      {distributors.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Data apertura</label>
                    <Input type="date" value={formData.data_apertura_switch || ''}
                           onChange={(e) => setFormData({ ...formData, data_apertura_switch: e.target.value })} />
                  </div>
                </>
              )}

              {action === 'reject_bo' && (
                <div>
                  <label className="text-xs text-slate-500">Motivo respingimento *</label>
                  <Textarea value={formData.motivo || ''}
                            onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                            rows={4} data-testid="motivo-input"
                            placeholder="Descrivi cosa deve correggere il commerciale..." />
                </div>
              )}

              {action === 'activate_manual' && (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                    <Warning size={14} className="inline mr-1" />
                    Stai forzando l'attivazione senza match RCU/SII. L'operazione sarà loggata.
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Mese competenza (YYYY-MM) *</label>
                    <Input type="month" value={formData.mese_competenza_sii || ''}
                           onChange={(e) => setFormData({ ...formData, mese_competenza_sii: e.target.value })}
                           data-testid="mese-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Motivo (audit trail)</label>
                    <Textarea value={formData.motivo || ''}
                              onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                              rows={3} placeholder="es: match RCU non disponibile, conferma telefonica da distributore..." />
                  </div>
                </>
              )}

              {action === 'validate_bo' && (
                <div>
                  <label className="text-xs text-slate-500">Note verifica</label>
                  <Textarea value={formData.motivo || ''}
                            onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                            rows={3} placeholder="Note opzionali sulla verifica documenti" />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => { setAction(null); setActive(null); }}>Annulla</Button>
                <Button onClick={runAction} className="bg-orange-500 hover:bg-orange-600"
                        data-testid="confirm-action-btn">Conferma</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RCU Upload Dialog */}
      <Dialog open={action === 'upload_rcu'} onOpenChange={(o) => { if (!o) { setAction(null); setRcuFile(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Carica CSV RCU — Match POD attivati</DialogTitle>
            <DialogDescription>
              Il sistema abbinerà i POD del CSV con i contratti in stato "In Attivazione" e li porterà in "Attivato".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-2">
              Il CSV deve contenere una colonna <code>pod</code> (o <code>pod_code</code>).
              I contratti in stato "In Attivazione" con POD presente nel file verranno automaticamente marcati come "Attivato".
            </div>
            <div>
              <label className="text-xs text-slate-500">Mese competenza *</label>
              <Input type="month" value={formData.mese_competenza_sii || ''}
                     onChange={(e) => setFormData({ ...formData, mese_competenza_sii: e.target.value })}
                     data-testid="rcu-mese-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500">File CSV *</label>
              <Input type="file" accept=".csv"
                     onChange={(e) => setRcuFile(e.target.files[0])}
                     data-testid="rcu-file-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => { setAction(null); setRcuFile(null); }}>Annulla</Button>
              <Button onClick={uploadRcu} className="bg-orange-500 hover:bg-orange-600"
                      data-testid="rcu-upload-submit">
                <Upload size={14} className="mr-1" /> Carica e match
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
